import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  TextField,
  Paper,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Breadcrumbs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Send as SendIcon,
  Reply as ReplyIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  PriorityHigh as HighPriorityIcon,
  Schedule as PendingIcon,
  CheckCircle as ResolvedIcon,
  Cancel as ClosedIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// API imports
import TicketsApiService from "../../../../infrastructure/api/tickets";
import FileUploader from "../../../components/shared/uploader/FileUploader";

const TicketDetail = ({ isAdmin = false }) => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [attachmentUrls, setAttachmentUrls] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState({
    status: "",
    assignedToUserId: "",
    adminNote: "",
  });

  useEffect(() => {
    if (ticketId) {
      loadTicketDetail();
    }
  }, [ticketId]);

  useEffect(() => {
    scrollToBottom();
  }, [ticket?.messages]);

  const loadTicketDetail = async () => {
    setLoading(true);
    try {
      const response = await TicketsApiService.getTicketDetail(ticketId);
      if (response.isSuccess) {
        setTicket(response.data);
      } else {
        toast.error(response.message || "خطا در دریافت جزئیات تیکت");
      }
    } catch (error) {
      toast.error("خطا در ارتباط با سرور");
      console.error("Error loading ticket detail:", error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) {
      toast.warning("لطفاً پیام خود را وارد کنید");
      return;
    }

    setSendingMessage(true);
    try {
      const messageData = {
        message: newMessage,
        attachmentUrls: attachmentUrls || "",
      };

      const response = isAdmin
        ? await TicketsApiService.addAdminMessage(ticketId, messageData)
        : await TicketsApiService.addMessage(ticketId, messageData);

      if (response.isSuccess) {
        setNewMessage("");
        setAttachmentUrls("");
        loadTicketDetail(); // Reload to show new message
        toast.success("پیام با موفقیت ارسال شد");
      } else {
        toast.error(response.message || "خطا در ارسال پیام");
      }
    } catch (error) {
      toast.error("خطا در ارتباط با سرور");
      console.error("Error sending message:", error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus.status) {
      toast.warning("لطفاً وضعیت جدید را انتخاب کنید");
      return;
    }

    try {
      const statusData = {
        status: parseInt(newStatus.status),
        assignedToUserId: newStatus.assignedToUserId ? parseInt(newStatus.assignedToUserId) : null,
        adminNote: newStatus.adminNote,
      };

      const response = await TicketsApiService.updateTicketStatus(ticketId, statusData);
      if (response.isSuccess) {
        setStatusDialogOpen(false);
        setNewStatus({ status: "", assignedToUserId: "", adminNote: "" });
        loadTicketDetail();
        toast.success("وضعیت تیکت با موفقیت به‌روزرسانی شد");
      } else {
        toast.error(response.message || "خطا در به‌روزرسانی وضعیت");
      }
    } catch (error) {
      toast.error("خطا در ارتباط با سرور");
      console.error("Error updating status:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString("fa-IR");
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "open":
        return <PendingIcon color="warning" />;
      case "inprogress":
        return <PendingIcon color="info" />;
      case "resolved":
        return <ResolvedIcon color="success" />;
      case "closed":
        return <ClosedIcon color="error" />;
      default:
        return <PendingIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "open":
        return "warning";
      case "inprogress":
        return "info";
      case "resolved":
        return "success";
      case "closed":
        return "error";
      default:
        return "default";
    }
  };

  const getPriorityIcon = (priority) => {
    if (priority >= 4) return <HighPriorityIcon color="error" />;
    if (priority >= 3) return <HighPriorityIcon color="warning" />;
    return null;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ mr: 2 }}>در حال دریافت جزئیات تیکت...</Typography>
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box p={3}>
        <Alert severity="error">تیکت یافت نشد</Alert>
        <Box mt={2}>
          <Button variant="outlined" onClick={() => navigate("/SLN/Tickets")}>
            بازگشت به لیست تیکت‌ها
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Typography
          color="text.secondary"
          sx={{ cursor: "pointer" }}
          onClick={() => navigate("/SLN/Tickets")}
        >
          پشتیبانی
        </Typography>
        <Typography color="text.primary">تیکت #{ticket.id}</Typography>
      </Breadcrumbs>

      {/* Ticket Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="h5" gutterBottom>
                {ticket.title}
              </Typography>
              <Box display="flex" gap={1} alignItems="center" mb={1}>
                {getStatusIcon(ticket.statusTitle)}
                <Chip
                  label={ticket.statusTitle}
                  color={getStatusColor(ticket.statusTitle)}
                />
                <Chip label={ticket.ticketCategoryTitle} variant="outlined" />
                <Box display="flex" alignItems="center">
                  {getPriorityIcon(ticket.priority)}
                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                    اولویت: {ticket.priorityTitle}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                ایجاد شده در {formatDate(ticket.createOn)} توسط {ticket.userName}
              </Typography>
              {ticket.assignedToUserName && (
                <Typography variant="body2" color="text.secondary">
                  مسئول: {ticket.assignedToUserName}
                </Typography>
              )}
            </Box>
            {isAdmin && (
              <Button
                variant="outlined"
                onClick={() => setStatusDialogOpen(true)}
              >
                تغییر وضعیت
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {ticket.description}
          </Typography>
        </CardContent>
      </Card>

      {/* Messages */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            پیام‌ها ({ticket.messages?.length || 0})
          </Typography>

          <Box sx={{ maxHeight: 400, overflowY: "auto", mb: 2 }}>
            {ticket.messages?.map((message, index) => (
              <Box key={message.id} sx={{ mb: 2 }}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    bgcolor: message.isFromAdmin ? "primary.lighter" : "grey.50",
                  }}
                >
                  <Box display="flex" alignItems="center" mb={1}>
                    <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                      {message.isFromAdmin ? (
                        <AdminIcon fontSize="small" />
                      ) : (
                        <PersonIcon fontSize="small" />
                      )}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2">
                        {message.userName}
                        {message.isFromAdmin && (
                          <Chip
                            label="ادمین"
                            size="small"
                            color="primary"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(message.createOn)}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {message.message}
                  </Typography>
                  {message.attachments && message.attachments.length > 0 && (
                    <Box mt={1}>
                      <Typography variant="caption" color="text.secondary">
                        پیوست‌ها:
                      </Typography>
                      {message.attachments.map((attachment, attIndex) => (
                        <Box key={attIndex} sx={{ mt: 0.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => window.open(attachment.fileUrl, "_blank")}
                          >
                            {attachment.fileName || `پیوست ${attIndex + 1}`}
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
                {index < ticket.messages.length - 1 && <Divider sx={{ my: 1 }} />}
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </Box>

          {/* Send Message */}
          {ticket.statusTitle?.toLowerCase() !== "closed" && (
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="پیام خود را وارد کنید..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && e.ctrlKey) {
                    handleSendMessage();
                  }
                }}
              />
              
              <Box display="flex" gap={1} alignItems="flex-start">
                <Box flex={1}>
                  <FileUploader
                    moduleName={"SLN/Tickets"}
                    maxFiles={3}
                    label="فایل پیوست (اختیاری)"
                    onChange={(value) => setAttachmentUrls(value || "")}
                    baseUrl={ticket?.baseUrl}
                    placeholder="فایل‌های مورد نظر را انتخاب کنید"
                    accept="image/*,.pdf,.doc,.docx,.zip,.rar"
                    helperText="حداکثر 3 فایل - فرمت‌های مجاز: تصاویر، PDF، Word، Zip"
                    value={attachmentUrls}
                  />
                </Box>
                <Button
                  variant="contained"
                  onClick={handleSendMessage}
                  disabled={sendingMessage || !newMessage.trim()}
                  startIcon={sendingMessage ? <CircularProgress size={20} /> : <SendIcon />}
                  sx={{ minWidth: 120, mt: 3 }}
                >
                {sendingMessage ? "در حال ارسال..." : "ارسال"}
              </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Attachments */}
      {ticket.attachments && ticket.attachments.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              پیوست‌های تیکت
            </Typography>
            <Grid container spacing={2}>
              {ticket.attachments.map((attachment, index) => (
                <Grid item xs={12} sm={6} md={4} key={attachment.id}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => window.open(attachment.fileUrl, "_blank")}
                    startIcon={<ReplyIcon />}
                  >
                    {attachment.fileName || `پیوست ${index + 1}`}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>تغییر وضعیت تیکت</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>وضعیت جدید *</InputLabel>
                <Select
                  value={newStatus.status}
                  onChange={(e) => setNewStatus({ ...newStatus, status: e.target.value })}
                  label="وضعیت جدید *"
                >
                  <MenuItem value="1">باز</MenuItem>
                  <MenuItem value="2">در حال انجام</MenuItem>
                  <MenuItem value="3">در انتظار پاسخ کاربر</MenuItem>
                  <MenuItem value="4">حل شده</MenuItem>
                  <MenuItem value="5">بسته شده</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="یادداشت ادمین (اختیاری)"
                multiline
                rows={2}
                value={newStatus.adminNote}
                onChange={(e) => setNewStatus({ ...newStatus, adminNote: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>انصراف</Button>
          <Button
            onClick={handleUpdateStatus}
            variant="contained"
            disabled={!newStatus.status}
          >
            به‌روزرسانی
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TicketDetail;